import CircularProgress from "@mui/material/CircularProgress";

import { Box } from "ui/box/box";

import { colors } from "design-tokens";

const SIZE_PX = { xs: 12, sm: 16, md: 24, lg: 32, xl: 48 } as const;

export type SpinnerSize = keyof typeof SIZE_PX;

export type SpinnerProps = {
  size?: SpinnerSize | number;
  color?: string;
  emptyColor?: string;
  thickness?: string | number;
  speed?: string;
  label?: string;
  className?: string;
} & Record<`data-${string}`, unknown>;

const toThickness = (value: SpinnerProps["thickness"]): number => {
  if (typeof value === "number") return value;
  const parsed = typeof value === "string" ? Number.parseFloat(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 4;
};

export const Spinner = ({
  size = "xl",
  color = colors.blue[500],
  emptyColor = colors.neutrals[200],
  thickness,
  speed = "0.65s",
  label = "Loading.",
  className,
  ...rest
}: SpinnerProps) => {
  const diameter = typeof size === "number" ? size : SIZE_PX[size];
  const strokeThickness = toThickness(thickness);

  return (
    <Box
      className={className}
      sx={{ position: "relative", display: "inline-flex", width: diameter, height: diameter }}
      {...rest}>
      <CircularProgress
        variant="determinate"
        value={100}
        color="inherit"
        size={diameter}
        thickness={strokeThickness}
        aria-hidden
        sx={{ color: emptyColor, position: "absolute", left: 0, top: 0 }}
      />
      <CircularProgress
        variant="indeterminate"
        disableShrink
        color="inherit"
        size={diameter}
        thickness={strokeThickness}
        aria-label={label}
        sx={{ color, animationDuration: speed }}
      />
    </Box>
  );
};
